import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { ArrowRight, CornerDownLeft, History, X, Search as SearchIcon, UserRound, Building2, Ruler, Hash, MessageCircle, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { getQueryHistory, pushQuery, removeQuery } from "../lib/recent";
import { highlightText } from "../lib/highlight";

const norm = (s) => String(s || "").toLowerCase();

/* 每个结果分组的视觉口径：图标 + 底色。新增分组只需在这里补一行。 */
const GROUP_META = {
  leads: { icon: UserRound, tone: "bg-amber-50 text-amber-600", zh: "客户", en: "Customers" },
  projects: { icon: Building2, tone: "bg-emerald-50 text-emerald-600", zh: "工程项目", en: "Projects" },
  designProjects: { icon: Ruler, tone: "bg-violet-50 text-violet-600", zh: "设计项目", en: "Design projects" },
  channels: { icon: Hash, tone: "bg-indigo-50 text-indigo-600", zh: "聊天频道", en: "Chat channels" },
  staff: { icon: MessageCircle, tone: "bg-sky-50 text-sky-600", zh: "同事（Enter 私聊）", en: "Colleagues (Enter → DM)" },
};

/* 顶部全局搜索（⌘K / Ctrl+K）
 *
 * 需求沿革（这份注释解释「为什么现在是这个样子」，改动前务必读完）：
 *   · 2026-10-16（SYS-002）：客户线索页同时有顶部搜索和列表搜索，需求方分不清区别，
 *     于是把顶部搜索**收窄成只找页面**，跨模块结果全部移除。
 *   · 2026-10-19（SYS-006）：客户反馈「⌘K 输入客户姓名显示『没有匹配的页面』」，搜不到客户；
 *     同一天用户明确要求：**放开之前的限制，恢复原来的跨模块搜索**。
 *   · 2026-10-20 用户口径（当前实现）：
 *       ① 恢复跨模块搜索（客户 / 项目 / 频道 / 同事 / 页面）；
 *       ② 结果**严格按账号权限**返回 —— 后端 /search/global 用与各模块列表同一套 scope；
 *       ③ 项目按角色分流：设计部账号搜到的是设计项目（跳 /design/projects/:id），
 *          工程侧账号搜到的是工程项目（跳 /project/:id），管理员 / CEO 两边都能搜到；
 *       ④ 输入框加宽，让占位文字（中文 / 英文）在未输入时完整显示，不被截断。
 *   · 2026-10-20 用户口径（当前实现，覆盖上一条的「可展开」）：**顶部搜索只给最接近的前 10 个**，
 *     不再提供「展开显示更多」——顶部搜索的定位是「快速定位一条记录并跳过去」，
 *     把 20 条一次性铺开会让最相关的那条被淹没。模糊匹配（子串 / 多关键词 / 电话写法容错）
 *     由后端完成，前端只按后端返回的相关性顺序渲染前 10 条。
 *
 * 与「列表内搜索」的分工仍然成立：顶部搜索是「跨模块找一条记录并跳过去」，
 * 列表搜索是「在当前这屏表格里按关键词过滤」。两者不再互斥，也不再互相冒充。
 */

const DEBOUNCE_MS = 220;
/* 【SYS-006 补充，2026-10-20 用户口径】顶部搜索**每类只给最接近的前 10 条**，不做「展开更多」。
   为什么不再保留 20 条 / 展开按钮：顶部搜索的用途是「跨模块快速找到那一条并跳过去」，
   单字模糊搜索会命中很多，把 20 条铺开会把最相关的一条淹掉；要翻更多请去对应模块的列表搜索。
   排序由后端 _rank（完全匹配 > 前缀 > 词首 > 包含 > 电话数字包含）保证。 */
const SEARCH_PAGE_SIZE = 10;
/* 请求条数 = 展示条数：不再多取隐藏的那 10 条，减少一次搜索的传输与渲染开销。 */
const SEARCH_MAX_ROWS = 10;

export default function GlobalSearch({ open, onOpenChange, pages, canSee }) {
  const { lang, user, api } = useApp();
  const nav = useNavigate();
  const zh = lang === "zh";
  /* 键位提示按平台显示 ⌘K / Ctrl K（与 Layout 顶栏同一判断口径）：
     2026-10-21 顶栏改成纯图标按钮后，键位提示搬到弹窗内的「可搜索范围」说明条，
     这里需要自己判断一次平台，避免在 Windows 上显示 ⌘。 */
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  const [q, setQ] = useState("");
  const [history, setHistory] = useState(() => getQueryHistory(user?.username));
  const [remote, setRemote] = useState(null);   // {categories:[...], q}
  const [busy, setBusy] = useState(false);
  const reqId = useRef(0);

  /* 权限过滤（用户 2026-10-19 追加要求）：
     全局搜索只能搜到「当前账号有权限访问的页面」，没有权限的菜单既不能出现在结果里，也不能靠输入路由猜出来。
     过滤逻辑由 Layout 以 canSee 传入（与侧栏菜单同一套 vis/moduleGranted/moduleDenied/pathDenied 判定），
     这里再做一次防御性过滤：即便调用方漏传过滤后的列表，也不会把越权页面暴露给用户。 */
  const allowedPages = useMemo(() => (typeof canSee === "function" ? pages.filter((p) => canSee(p)) : pages), [pages, canSee]);

  useEffect(() => {   // 关闭时把本次输入记入「最近搜索」（≥2 字，即使没选任何结果）
    if (!open) { if (q.trim().length >= 2) pushQuery(user?.username, q); setQ(""); setRemote(null); }
    else { setHistory(getQueryHistory(user?.username)); }
  }, [open, user?.username]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => { setHistory(getQueryHistory(user?.username)); };
    window.addEventListener("ali-recent", h);
    return () => window.removeEventListener("ali-recent", h);
  }, [user?.username]);

  const term = q.trim();
  const nq = norm(term);
  // 【SYS-006 补充，2026-10-20 用户口径】单字搜索放开：「只记得一个姓（杨）也该搜得到」。
  // 原来少于 2 个字符直接不发请求，中文单字（信息量远大于英文单字母）就被挡在门外。
  // 现在 1 个字符起搜；结果偏多的问题由「后端按相关性排序 + 每类只取前 10 条」解决 ——
  // 排序把完全匹配 / 前缀匹配顶到最前，用户要的那条基本都在第一屏。

  /* 跨模块结果：交给后端按权限查（客户 / 工程项目 / 设计项目 / 频道 / 同事）。
     为什么不在前端用 visibleProjects + /crm/leads 本地过滤：
       · 项目列表接口最多返回 1000 条、客户列表 2000 条，本地过滤会漏；
       · 设计项目的可见性还要看「未完成阶段 / 任务负责人」，规则只在后端。
     并发保护：输入很快时丢弃过期响应，避免旧关键词的结果盖住新关键词的。 */
  useEffect(() => {
    if (!open) return undefined;
    // 单字起搜：中文一个字也能定位（姓、门牌号开头、楼栋号）；空输入仍不发请求。
    if (nq.length < 1) { setRemote(null); setBusy(false); return undefined; }
    const my = ++reqId.current;
    setBusy(true);
    const t = setTimeout(() => {
      // 只取 10 条：与展示条数一致，不多取隐藏结果（用户口径：顶部只给最接近的 10 个）。
      api.get("/search/global", { params: { q: term, limit: SEARCH_MAX_ROWS } })
        .then(({ data }) => { if (my === reqId.current) setRemote(data); })
        .catch(() => { if (my === reqId.current) setRemote({ categories: [] }); })
        .finally(() => { if (my === reqId.current) setBusy(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, term, nq.length, api]);

  // 只搜页面：按菜单名称或路由匹配；空输入时展示前 8 个页面作为快捷入口（均限定在有权限的页面内）
  const pageHits = useMemo(() => (nq ? allowedPages.filter((p) => norm(p.label).includes(nq) || norm(p.to).includes(nq)) : allowedPages.slice(0, 8)).slice(0, 10), [allowedPages, nq]);
  const remember = () => pushQuery(user?.username, q);

  // 后端返回的分组（保持后端顺序：客户 → 工程 → 设计 → 频道 → 同事）
  const remoteCats = useMemo(() => {
    if (!remote || norm(remote.q) !== nq) return [];
    return (remote.categories || []).filter((c) => (c.items || []).length > 0);
  }, [remote, nq]);
  // 底部「跨模块命中 N 条」用后端 total 累加：items 已被截断到每类 10 条，
  // 拿 items.length 求和会低报（明明 30 条却显示 10 条），total 才是真实命中数。
  const remoteCount = remoteCats.reduce((s, c) => s + (c.total ?? c.items?.length ?? 0), 0);

  const close = () => onOpenChange(false);
  const openRoute = (route) => { remember(); close(); if (route) nav(route); };
  /* 同事没有独立详情页：沿用旧版行为 —— 选中后创建 / 复用私聊再跳过去。
     失败（如对方账号异常）时退回聊天首页，不让点击无反应。 */
  const openStaff = async (it) => {
    remember(); close();
    try { const { data } = await api.post("/chat/channels", { type: "dm", members: [it.id] }); nav(`/chat/c/${data.id}`); }
    catch { nav("/chat"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 弹窗宽度（2026-10-21 用户口径，最终方案）：
          顶栏改成「纯放大镜图标」后，提示词不再需要在顶栏挤空间，全部交给这个浮层，
          宽度自己做主。这里从 max-w-2xl(672px) 放宽到 max-w-3xl(768px)，
          让「搜索客户 / 项目 / 频道 / 同事 / 页面」这类整句在中英文下都留有余量，
          也给下面的分组标题和结果副标题更多横向空间。窄屏仍是 w-[calc(100vw-2rem)] 自适应。 */}
      <DialogContent aria-describedby={undefined} className="overflow-hidden p-0 sm:p-0 gap-0 w-[calc(100vw-2rem)] max-w-3xl top-[18%] translate-y-0 data-[state=open]:slide-in-from-top-[2%]" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">{zh ? "全局搜索" : "Global search"}</DialogTitle>
      {/* 需求背景 / 修改说明（本次修复）：
          原实现在 Command 上写 `[&_[cmdk-input]]:h-12` 想把输入框高度改成 48px，但这个变体
          选择器生成的是 `.x [cmdk-input]`（0,2,0），而输入框自身 className 里的 `h-10`
          是（0,1,0）——两者都命中同一个 h 属性，后者特异性更高，实际生效的是 h-10(40px)，
          再叠加 py-3(上下各 12px) 且 box-sizing:border-box，浏览器按内容盒算出 12+30+12=54px。
          而输入框外层 [cmdk-input-wrapper] 只有 55px 高，导致输入框整体下移、上边框顶出弹窗
          上沿、下边框与 wrapper 的 border-b 分割线/聚焦环错位（表现为"选中后边框超出弹窗"）。
          修复：用行内 style 直接覆盖（优先级最高、最直接），把输入框高度精确锁成 48px，
          并让其上下 padding 对称；这样输入框严格落在 wrapper 内，与分割线、聚焦环对齐。
          这里不用 Tailwind 类名（!important 变体同样依赖特异性，容易再次失效）。 */}
      <Command shouldFilter={false} className="rounded-2xl [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5" data-testid="global-search">
        <CommandInput data-testid="global-search-input" style={{ height: 48, paddingTop: 12, paddingBottom: 12, boxSizing: "border-box" }}
          value={q} onValueChange={setQ}
          placeholder={zh ? "搜索客户 / 项目 / 频道 / 同事 / 页面" : "Search customers / projects / channels / people / pages"} />
        {/* 可搜索范围说明条（2026-10-21 用户口径「提示词完整展示」的落点）：
            顶栏改成纯放大镜后，这里才是唯一需要把「到底能搜什么」讲清楚的地方，
            所以给一行常驻说明，且宽度由弹窗决定、中英文都整句显示、不截断。
            同时把键位提示从顶栏搬到这（顶栏不再为它占位），用户仍能学会 ⌘K / Ctrl+K。
            只在未输入时显示：一旦开始输入，用户已经知道自己在搜什么，这一行就不再占高度。 */}
        {!nq && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 border-b border-slate-100 bg-slate-50/60 text-[11px] text-slate-500" data-testid="global-search-scope">
            <span className="font-semibold text-slate-600">{zh ? "可搜索：" : "Searches:"}</span>
            <span>{zh ? "客户 · 工程项目 · 设计项目 · 聊天频道 · 同事 · 页面" : "Customers · Projects · Design projects · Channels · People · Pages"}</span>
            <span className="ml-auto inline-flex items-center gap-1 shrink-0 text-slate-400">
              <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-white font-mono text-[10px]">{isMac ? "⌘K" : "Ctrl K"}</kbd>
              {zh ? "随时唤起" : "to open"}
            </span>
          </div>
        )}
        <CommandList className="max-h-[60vh]">
          <CommandEmpty data-testid="global-search-empty">
            {busy ? (zh ? "搜索中…" : "Searching…")
              : (zh ? `未找到包含「${term}」的客户 / 项目 / 页面` : `No customers, projects or pages match “${term}”`)}
          </CommandEmpty>
          {!nq && history.length > 0 && (
            <CommandGroup heading={zh ? "最近搜索" : "Recent searches"} data-testid="gs-history-group">
              {history.map((h, i) => (
                <CommandItem key={`hs-${h}`} value={`hs-${i}`} onSelect={() => setQ(h)} data-testid={`gs-hist-${i}`} className="gap-3 rounded-xl group/h">
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-slate-50 text-slate-400"><SearchIcon className="h-3.5 w-3.5" /></span>
                  <span className="flex-1 truncate">{h}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); removeQuery(user?.username, h); }} onPointerDown={(e) => e.stopPropagation()} data-testid={`gs-hist-remove-${i}`} className="h-6 w-6 rounded-md flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50" aria-label="remove"><X className="h-3.5 w-3.5" /></button>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {/* 跨模块结果（客户 / 工程项目 / 设计项目 / 频道 / 同事）—— 后端已按权限过滤，
              前端只按返回的 route 跳转，不做二次猜测。 */}
          {remoteCats.map((cat) => {
            const meta = GROUP_META[cat.key] || GROUP_META.leads;
            const Icon = meta.icon;
            // 【2026-10-20 用户口径】每类只展示最接近的前 SEARCH_PAGE_SIZE(10) 条，不再有「展开更多」。
            // 后端已按相关性排好序（total 仍回传，仅用于底部「命中 N 条」，不参与截断判断）。
            return (
              <CommandGroup key={cat.key} heading={zh ? meta.zh : meta.en} data-testid={`gs-group-${cat.key}`}>
                {cat.items.slice(0, SEARCH_PAGE_SIZE).map((it) => (
                  <CommandItem key={`${cat.key}-${it.id}`} value={`${cat.key}-${it.id}`}
                    onSelect={() => (cat.key === "staff" ? openStaff(it) : openRoute(it.route))}
                    data-testid={`gs-${cat.key}-${it.id}`} className="gap-3 rounded-xl">
                    <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.tone}`}><Icon className="h-4 w-4" /></span>
                    <span className="flex-1 min-w-0">
                      {/* 【SYS-006 ③】搜索命中项高亮：老板原话是「搜索结果高亮匹配项」，
                          之前只给客户列表做了高亮，⌘K 这里仍是纯文本 —— 同一个词在列表里标黄、
                          在全局搜索里毫无反应，观感像「没搜到」。这里复用 lib/highlight 的同一套切词
                          （含电话写法容错，与后端召回口径一致），标题与副标题都高亮，
                          让「这条为什么会被搜出来」一眼可见。 */}
                      <span className="block truncate text-slate-800">{highlightText(it.title, term)}</span>
                      {it.sub && <span className="block text-[11px] text-slate-400 truncate">{highlightText(it.sub, term)}</span>}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
          {pageHits.length > 0 && (
            <CommandGroup heading={zh ? "页面" : "Pages"}>
              {pageHits.map((p) => (
                <CommandItem key={`pg-${p.to}`} value={`pg-${p.to}`} onSelect={() => openRoute(p.to)} data-testid={`gs-page-${p.to.replace(/\//g, "-").replace(/^-/, "")}`} className="gap-3 rounded-xl">
                  <span className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><p.icon className="h-4 w-4" /></span>
                  <span className="flex-1 truncate">{p.label}</span>
                  <span className="text-[11px] text-slate-400 truncate max-w-[180px]">{p.to}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        <div className="flex items-center gap-3 px-3 py-2 border-t border-slate-100 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px]">↑↓</kbd>{zh ? "选择" : "navigate"}</span>
          <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px]"><CornerDownLeft className="h-3 w-3 inline" /></kbd>{zh ? "打开" : "open"}</span>
          <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px]">esc</kbd>{zh ? "关闭" : "close"}</span>
          {/* 让用户一眼知道「这里搜出来的是什么范围」，而不是再去猜它和列表搜索的区别。 */}
          <span className="ml-auto inline-flex items-center gap-1">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <History className="h-3 w-3" />}
            {remoteCount > 0 ? (zh ? `跨模块命中 ${remoteCount} 条` : `${remoteCount} results`) : (zh ? "按你的权限搜索" : "Within your access")}
          </span>
        </div>
      </Command>
      </DialogContent>
    </Dialog>
  );
}
